import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSettings } from '../../contexts/SettingsContext';

/**
 * HorizontalBarChart - SVG-based horizontal bar chart
 * Supports dark/light mode via theme context
 * Uses dynamic theme colors from settings
 */

const CHART_PALETTE = [
  '#3B5BDB', '#20C997', '#845EF7', '#F06595', '#FF922B',
  '#228BE6', '#12B886', '#BE4BDB', '#FA5252', '#FAB005',
  '#15AABF', '#40C057', '#7950F2', '#E64980', '#FD7E14',
];

const HorizontalBarChart = ({
  data = [],
  title = '',
  maxItems = 10,
  barHeight = 28,
  showValues = true,
  colorMode = 'gradient', // 'gradient' | 'multi' | 'single'
  className = '',
}) => {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings?.theme?.colors?.secondary || '#20C997';

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data].sort((a, b) => b.count - a.count).slice(0, maxItems);
    const max = sorted[0]?.count || 1;
    return sorted.map((item, index) => ({
      ...item,
      percentage: (item.count / max) * 100,
      color: colorMode === 'multi'
        ? CHART_PALETTE[index % CHART_PALETTE.length]
        : colorMode === 'gradient'
          ? `color-mix(in srgb, ${primaryColor} ${100 - (index / sorted.length) * 40}%, ${secondaryColor})`
          : primaryColor,
    }));
  }, [data, maxItems, colorMode, primaryColor, secondaryColor]);

  // Animation state
  const [animated, setAnimated] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setAnimated(false);
    timerRef.current = requestAnimationFrame(() => {
      timerRef.current = requestAnimationFrame(() => {
        setAnimated(true);
      });
    });
    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
    };
  }, [chartData]);

  if (!chartData.length) {
    return (
      <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">No data available</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {title && (
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 uppercase tracking-wider">
          {title}
        </h3>
      )}

      <div className="space-y-3">
        {chartData.map((item, index) => (
          <div
            key={index}
            className="group"
            style={{
              opacity: animated ? 1 : 0,
              transform: animated ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 0.35s ease-out ${index * 0.06}s, transform 0.35s ease-out ${index * 0.06}s`,
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[60%] group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                {item.name}
              </span>
              {showValues && (
                <span className="text-sm font-semibold text-gray-900 dark:text-white ml-2 flex-shrink-0">
                  {item.count}
                </span>
              )}
            </div>
            <div
              className="w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden"
              style={{ height: barHeight / 3 }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: animated ? `${item.percentage}%` : '0%',
                  background: colorMode === 'gradient'
                    ? `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`
                    : colorMode === 'multi'
                      ? item.color
                      : primaryColor,
                  opacity: 1 - (index * 0.05),
                  transition: `width ${0.6 + index * 0.08}s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HorizontalBarChart;
